import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Toaster, toast } from 'sonner';
import { insertSupabaseLead } from '../../lib/supabaseClient';

const TRANSLATIONS = {
  EN: {
    tagline: 'DRIVER RECRUITMENT · VAN DELIVERY FLEET',
    formTitle: 'Van Delivery Driver — Application Form',
    languageToggle: 'Français',

    sec1Title: 'Driver Information',
    fullName: 'Full Legal Name *',
    fullNamePlaceholder: 'Johnathan Smith',
    dob: 'Date of Birth',
    phone: 'Phone Number *',
    phonePlaceholder: '(647) 000-0000',
    email: 'Email Address *',
    emailPlaceholder: 'driver@example.com',
    homeAddress: 'Home Address',
    homeAddressPlaceholder: '123 Delivery Road, Apt 4',
    city: 'City',
    cityPlaceholder: 'Toronto / Brampton / Montreal',
    province: 'Province / State',
    country: 'Country',
    postalCode: 'Postal Code',
    postalCodePlaceholder: 'M5V 2T6',
    emergencyName: 'Emergency Contact Name',
    emergencyNamePlaceholder: 'Jane Smith',
    emergencyPhone: 'Emergency Contact Phone',
    emergencyPhonePlaceholder: '(647) 987-6543',

    sec2Title: 'Licensing & Driving Credentials',
    licenseNumber: "Driver's License Number *",
    licenseNumberPlaceholder: 'S8976-54321-09812',
    licenseClass: 'License Class',
    licenseClassPlaceholder: 'Class G / Class 1 / Class 3',
    provinceOfIssue: 'Province of Issue',
    provinceOfIssuePlaceholder: 'Ontario / Quebec',
    licenseExpiry: 'License Expiry Date',
    yearsDriving: 'Years Driving',
    years1_2: '1 - 2 years',
    years3_5: '3 - 5 years',
    years5_10: '5 - 10 years',
    years10Plus: '10+ years',
    endorsements: 'Endorsements / Restrictions',
    endorsementsPlaceholder: 'e.g. Air Brakes (Z), None',

    sec3Title: 'Availability & Experience',
    availability: 'Availability',
    availFullTime: 'Full-time',
    availPartTime: 'Part-time',
    availWeekend: 'Weekend Only',
    availCasual: 'Casual / On-Call',
    startDate: 'Earliest Start Date',
    experience: 'Van / Delivery Experience',
    experiencePlaceholder: 'Describe your previous delivery experience (e.g. Amazon, Courier, Sprinter Van, Cargo Van)...',

    sec4Title: 'Required Documents',
    scanOrSnap: 'Scan or snap photo',
    dlFrontTitle: "Photo ID #1 — Driver's License (Front)",
    dlFrontDesc: 'Front side photo or scan.',
    dlBackTitle: "Photo ID #1 — Driver's License (Back)",
    dlBackDesc: 'Back side photo or scan.',
    govFrontTitle: 'Photo ID #2 — Government Photo ID (Front)',
    govFrontDesc: 'Passport, PR card, or govt photo ID (Front).',
    govBackTitle: 'Photo ID #2 — Government Photo ID (Back)',
    govBackDesc: 'Passport, PR card, or govt photo ID (Back).',
    abstractTitle: 'Driving Record / Driving History',
    abstractDesc: 'Official driving abstract (MVR).',
    uploadBtn: 'Take Photo / Upload',
    attached: 'Attached',
    removeDoc: 'Document removed',
    fileExceeds: 'File exceeds 15MB size limit',

    sec5Title: 'Consent & Digital Signature',
    consentLabel: 'I consent to Iron Route Logistics Corp verifying my driving record and background, and confirm the information provided in this application is accurate. *',
    signature: 'Signature',
    clearPad: 'Clear Pad',
    drawHere: '✍️ Draw signature here with finger or mouse',
    typeSignature: 'Or type full legal signature name...',
    date: 'Date',

    submitBtn: 'Submit Application to Iron Route Dispatch',
    submitting: 'Submitting Application & Documents...',
    submitSuccess: 'Application Submitted Successfully!',

    errFullName: 'Please enter your Full Legal Name',
    errPhone: 'Please enter your Phone Number',
    errEmail: 'Please enter your Email Address',
    errLicense: "Please enter your Driver's License Number",

    appReceivedSub: 'VAN DELIVERY DRIVER APPLICATION RECEIVED',
    refNumber: 'Reference Number',
    driverName: 'Driver Name',
    licenseClassLabel: 'License Class',
    docsUploaded: 'Documents Uploaded',
    filesAttached: 'Files Attached',
    confirmationNote: 'Dispatch has received your completed application & documents. If you have questions, please reach out to',
    submitAnother: 'Submit Another Application'
  },
  FR: {
    tagline: 'RECRUTEMENT DES CHAUFFEURS · FLOTTE DE LIVRAISON EN FOURGONNETTE',
    formTitle: 'Chauffeur de Livraison en Fourgonnette — Formulaire de Candidature',
    languageToggle: 'English',

    sec1Title: 'Informations du Chauffeur',
    fullName: 'Nom Légal Complet *',
    fullNamePlaceholder: 'Jean Tremblay',
    dob: 'Date de Naissance',
    phone: 'Numéro de Téléphone *',
    phonePlaceholder: '(514) 000-0000',
    email: 'Adresse Courriel *',
    emailPlaceholder: 'chauffeur@exemple.com',
    homeAddress: 'Adresse Résidentielle',
    homeAddressPlaceholder: '123 Rue de la Livraison, App. 4',
    city: 'Ville',
    cityPlaceholder: 'Montréal / Québec / Laval / Gatineau',
    province: 'Province / État',
    country: 'Pays',
    postalCode: 'Code Postal',
    postalCodePlaceholder: 'H3B 1A1',
    emergencyName: "Nom du Contact d'Urgence",
    emergencyNamePlaceholder: 'Marie Tremblay',
    emergencyPhone: "Téléphone du Contact d'Urgence",
    emergencyPhonePlaceholder: '(514) 987-6543',

    sec2Title: 'Permis de Conduire et Qualifications',
    licenseNumber: 'Numéro de Permis de Conduire *',
    licenseNumberPlaceholder: 'T8976-54321-09812',
    licenseClass: 'Catégorie de Permis',
    licenseClassPlaceholder: 'Classe 5 / Classe 1 / Class G',
    provinceOfIssue: 'Province de Délivrance',
    provinceOfIssuePlaceholder: 'Québec / Ontario',
    licenseExpiry: "Date d'Expiration du Permis",
    yearsDriving: "Années d'Expérience de Conduite",
    years1_2: '1 - 2 ans',
    years3_5: '3 - 5 ans',
    years5_10: '5 - 10 ans',
    years10Plus: '10 ans et +',
    endorsements: 'Mentions / Restrictions',
    endorsementsPlaceholder: 'ex. Freins à air (F), Aucune',

    sec3Title: 'Disponibilité et Expérience',
    availability: 'Disponibilité',
    availFullTime: 'Temps plein',
    availPartTime: 'Temps partiel',
    availWeekend: 'Fins de semaine seulement',
    availCasual: 'Sur appel / Occasionnel',
    startDate: 'Date de Début la Plus Rapprochée',
    experience: 'Expérience de Livraison en Fourgonnette',
    experiencePlaceholder: 'Décrivez votre expérience de livraison antérieure (ex. Amazon, Messagerie, Fourgonnette Sprinter, Cargo)...',

    sec4Title: 'Documents Requis',
    scanOrSnap: 'Scannez ou prenez une photo',
    dlFrontTitle: "Pièce d'Identité #1 — Permis de Conduire (Recto)",
    dlFrontDesc: 'Photo ou scan du recto de votre permis de conduire.',
    dlBackTitle: "Pièce d'Identité #1 — Permis de Conduire (Verso)",
    dlBackDesc: 'Photo ou scan du verso de votre permis de conduire.',
    govFrontTitle: "Pièce d'Identité #2 — Pièce d'Identité Gouvernementale (Recto)",
    govFrontDesc: 'Passeport, carte de RP, ou carte officielle (Recto).',
    govBackTitle: "Pièce d'Identité #2 — Pièce d'Identité Gouvernementale (Verso)",
    govBackDesc: 'Passeport, carte de RP, ou carte officielle (Verso).',
    abstractTitle: 'Dossier de Conduite / Historique MVR',
    abstractDesc: 'Dossier de conduite officiel (SAAQ / MTO).',
    uploadBtn: 'Prendre Photo / Téléverser',
    attached: 'Ajouté',
    removeDoc: 'Document retiré',
    fileExceeds: 'Le fichier dépasse la limite de 15 Mo',

    sec5Title: 'Consentement et Signature Numérique',
    consentLabel: "Je consens à ce qu'Iron Route Logistics Corp vérifie mon dossier de conduite et mes antécédents, et confirme que les informations fournies dans cette candidature sont exactes. *",
    signature: 'Signature',
    clearPad: 'Effacer',
    drawHere: '✍️ Dessinez votre signature ici avec le doigt ou la souris',
    typeSignature: 'Ou tapez votre nom légal complet...',
    date: 'Date',

    submitBtn: "Soumettre la Candidature à l'Expédition Iron Route",
    submitting: 'Envoi de la Candidature et des Documents...',
    submitSuccess: 'Candidature Soumise avec Succès !',

    errFullName: 'Veuillez entrer votre Nom Légal Complet',
    errPhone: 'Veuillez entrer votre Numéro de Téléphone',
    errEmail: 'Veuillez entrer votre Adresse Courriel',
    errLicense: 'Veuillez entrer votre Numéro de Permis de Conduire',

    appReceivedSub: 'CANDIDATURE DE CHAUFFEUR DE LIVRAISON REÇUE',
    refNumber: 'Numéro de Référence',
    driverName: 'Nom du Chauffeur',
    licenseClassLabel: 'Catégorie de Permis',
    docsUploaded: 'Documents Téléversés',
    filesAttached: 'Fichiers Joints',
    confirmationNote: "Le service d'expédition a bien reçu votre candidature et vos documents. Si vous avez des questions, veuillez nous contacter à",
    submitAnother: 'Soumettre Une Autre Candidature'
  }
};

const getRequiredDocuments = (t) => [
  {
    id: 'drivers_license_front',
    title: t.dlFrontTitle,
    description: t.dlFrontDesc,
    required: true,
    icon: 'fa-id-card'
  },
  {
    id: 'drivers_license_back',
    title: t.dlBackTitle,
    description: t.dlBackDesc,
    required: true,
    icon: 'fa-id-card'
  },
  {
    id: 'government_id_front',
    title: t.govFrontTitle,
    description: t.govFrontDesc,
    required: false,
    icon: 'fa-passport'
  },
  {
    id: 'government_id_back',
    title: t.govBackTitle,
    description: t.govBackDesc,
    required: false,
    icon: 'fa-passport'
  },
  {
    id: 'driving_abstract',
    title: t.abstractTitle,
    description: t.abstractDesc,
    required: false,
    icon: 'fa-file-certificate'
  }
];

const CANADIAN_PROVINCES = [
  'Ontario',
  'Quebec',
  'Alberta',
  'British Columbia',
  'Manitoba',
  'Saskatchewan',
  'Nova Scotia',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Prince Edward Island',
  'Other / US State'
];

export default function DriverRecruitmentForm() {
  // Language State: 'EN' or 'FR'
  const [lang, setLang] = useState('EN');
  const t = TRANSLATIONS[lang] || TRANSLATIONS.EN;
  const requiredDocs = getRequiredDocuments(t);

  // Form State
  const [formData, setFormData] = useState({
    // 1. Driver Information
    fullName: '',
    dob: '',
    phone: '',
    email: '',
    homeAddress: '',
    city: '',
    province: 'Ontario',
    country: 'Canada',
    postalCode: '',
    emergencyContactName: '',
    emergencyContactPhone: '',

    // 2. Licensing & Credentials
    licenseNumber: '',
    licenseClass: 'Class G',
    provinceOfIssue: 'Ontario',
    licenseExpiryDate: '',
    yearsDriving: '3-5 years',
    endorsements: '',

    // 3. Availability
    availability: 'Full-time',
    earliestStartDate: new Date().toISOString().split('T')[0],
    vanDeliveryExperience: '',

    // 5. Consent & Signature
    consentGiven: true,
    signatureName: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  // Attached Documents
  const [documents, setDocuments] = useState({
    drivers_license_front: [],
    drivers_license_back: [],
    government_id_front: [],
    government_id_back: [],
    driving_abstract: []
  });

  // Geolocation State
  const [locationState, setLocationState] = useState({
    status: 'prompt', // 'prompt' | 'fetching' | 'captured' | 'denied' | 'error'
    latitude: null,
    longitude: null,
    accuracy: null,
    timestamp: null,
    addressString: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submittedLead, setSubmittedLead] = useState(null);
  const [validationError, setValidationError] = useState('');

  // Canvas Signature pad
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);

  // Auto-capture GPS Geolocation on mount
  useEffect(() => {
    captureGeolocation();
  }, []);

  const captureGeolocation = () => {
    if (!navigator.geolocation) {
      setLocationState({
        status: 'error',
        latitude: null,
        longitude: null,
        accuracy: null,
        timestamp: new Date().toISOString(),
        addressString: 'Geolocation not supported by browser'
      });
      return;
    }

    setLocationState(prev => ({ ...prev, status: 'fetching' }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setLocationState({
          status: 'captured',
          latitude: Number(latitude.toFixed(6)),
          longitude: Number(longitude.toFixed(6)),
          accuracy: Math.round(accuracy),
          timestamp: new Date(position.timestamp).toISOString(),
          addressString: `Lat: ${latitude.toFixed(4)}, Long: ${longitude.toFixed(4)} (±${Math.round(accuracy)}m)`
        });
        // GPS location captured silently in background
      },
      (error) => {
        console.warn('GPS location warning:', error.message);
        let errorMsg = 'Location permission denied';
        if (error.code === error.POSITION_UNAVAILABLE) errorMsg = 'Position unavailable';
        if (error.code === error.TIMEOUT) errorMsg = 'Location request timed out';

        setLocationState({
          status: 'denied',
          latitude: null,
          longitude: null,
          accuracy: null,
          timestamp: new Date().toISOString(),
          addressString: errorMsg
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (validationError) setValidationError('');
  };

const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width / height > maxWidth / maxHeight) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      img.onerror = (err) => reject(err);
      img.src = event.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

  const handleFileUpload = async (docTypeId, files) => {
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`File ${file.name} exceeds 20MB size limit.`);
        continue;
      }

      toast.info(`Compressing & attaching ${file.name}...`);

      try {
        const compressedDataUrl = await compressImage(file, 1200, 1200, 0.7);
        const approxSizeKb = (compressedDataUrl.length * 0.75 / 1024).toFixed(1);

        const fileObj = {
          id: 'doc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
          name: file.name,
          size: approxSizeKb + ' KB',
          type: file.type.includes('pdf') ? 'pdf' : 'image',
          dataUrl: compressedDataUrl,
          uploadedAt: new Date().toISOString()
        };

        setDocuments(prev => ({
          ...prev,
          [docTypeId]: [...(prev[docTypeId] || []), fileObj]
        }));
        toast.success(`Attached ${file.name} (${approxSizeKb} KB)`);
      } catch (err) {
        console.error('Image processing error:', err);
        toast.error(`Could not process ${file.name}`);
      }
    }
  };

  const removeDocument = (docTypeId, docId) => {
    setDocuments(prev => ({
      ...prev,
      [docTypeId]: prev[docTypeId].filter(d => d.id !== docId)
    }));
    toast.info('Document removed');
  };

  // Touch and Mouse Canvas Signature helpers
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    if (e.type === 'touchstart') {
      e.preventDefault();
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    if (e.type === 'touchmove') {
      e.preventDefault();
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);

    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#3b82f6';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        setSignatureImage(canvas.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureImage(null);
    }
  };

  // Bulletproof Submission Handler
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    setValidationError('');

    // Field validations with visual alert fallback
    if (!formData.fullName || !formData.fullName.trim()) {
      const msg = t.errFullName;
      setValidationError(msg);
      toast.error(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!formData.phone || !formData.phone.trim()) {
      const msg = t.errPhone;
      setValidationError(msg);
      toast.error(msg);
      return;
    }

    if (!formData.email || !formData.email.trim()) {
      const msg = t.errEmail;
      setValidationError(msg);
      toast.error(msg);
      return;
    }

    if (!formData.licenseNumber || !formData.licenseNumber.trim()) {
      const msg = t.errLicense;
      setValidationError(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);

    const refNumber = 'LEAD-' + new Date().getFullYear() + '-' + Math.floor(10000 + Math.random() * 90000);

    const newLead = {
      id: 'lead_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
      referenceNumber: refNumber,
      submittedAt: new Date().toISOString(),
      status: 'New Lead',
      company: 'IRON ROUTE LOGISTICS CORP',
      language: lang,
      formData: {
        ...formData,
        signatureName: formData.signatureName || formData.fullName
      },
      signatureImage: signatureImage || null,
      documents,
      location: locationState,
      comments: [
        {
          id: 'c_init',
          author: 'System',
          text: `Application submitted in ${lang === 'FR' ? 'French' : 'English'} via Iron Route Form from ${locationState.addressString || 'mobile web'}.`,
          timestamp: new Date().toISOString()
        }
      ]
    };

    try {
      // 0. Save application directly to Supabase database table
      try {
        await insertSupabaseLead(newLead);
      } catch (sbErr) {
        console.warn('Supabase lead submission note:', sbErr);
      }

      // 1. Store in localStorage for instant local reactivity on device
      const existing = JSON.parse(localStorage.getItem('driver_recruitment_leads') || '[]');
      const updated = [newLead, ...existing];
      localStorage.setItem('driver_recruitment_leads', JSON.stringify(updated));
      window.dispatchEvent(new Event('recruitment_lead_submitted'));

      // 2. Transmit payload to FastAPI Backend Service (/api/driver-recruitment/leads)
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.staging.integratedtech.ca';

      const firstName = (formData.fullName || 'Driver').split(' ')[0];
      const lastName = ((formData.fullName || '').split(' ').slice(1).join(' ') || 'Applicant') + ' [RECRUIT]';

      const demoPayload = {
        first_name: firstName,
        last_name: lastName,
        email: formData.email || 'driver@ironroute.com',
        company: 'IRON ROUTE LOGISTICS CORP',
        phone: formData.phone || 'N/A',
        role: 'Van Driver Applicant',
        fleet_size: '1',
        message: JSON.stringify(newLead)
      };

      const postEndpoints = Array.from(new Set([
        `${BACKEND_URL}/api/driver-recruitment/leads`,
        'https://api.staging.integratedtech.ca/api/driver-recruitment/leads',
        'https://api.integratedtech.ca/api/driver-recruitment/leads',
        `${BACKEND_URL}/api/marketing/demo-request`
      ])).filter(Boolean);

      for (const endpoint of postEndpoints) {
        try {
          await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: endpoint.includes('marketing') ? JSON.stringify(demoPayload) : JSON.stringify(newLead)
          });
        } catch (err) {
          // ignore individual fetch errors
        }
      }

      setSubmittedLead(newLead);
      toast.success(t.submitSuccess);
    } catch (err) {
      console.error('Submission error:', err);
      // Still display confirmation if local save worked
      setSubmittedLead(newLead);
      toast.success(t.submitSuccess);
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedLead) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-slate-900 border-slate-800 text-slate-100 shadow-2xl rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 p-8 text-center relative">
            
            {/* Language Switcher on Confirmation Screen */}
            <div className="flex justify-center mb-3">
              <button
                type="button"
                onClick={() => setLang(prev => prev === 'EN' ? 'FR' : 'EN')}
                className="inline-flex items-center gap-1.5 px-3 py-1 bg-black/25 hover:bg-black/40 text-amber-300 font-bold text-xs rounded-full border border-amber-400/30 transition-all cursor-pointer select-none"
              >
                <i className="fas fa-globe"></i>
                <span>{lang === 'EN' ? '🇨🇦 Français' : '🇨🇦 English'}</span>
              </button>
            </div>

            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">
              🚚
            </div>
            <h2 className="text-2xl font-bold">IRON ROUTE LOGISTICS CORP</h2>
            <p className="text-amber-100 text-xs mt-1 font-semibold uppercase tracking-wider">
              {t.appReceivedSub}
            </p>
          </div>

          <CardContent className="p-6 space-y-6">
            <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center text-sm border-b border-slate-700 pb-2">
                <span className="text-slate-400">{t.refNumber}</span>
                <span className="font-mono font-bold text-amber-400">{submittedLead.referenceNumber}</span>
              </div>
              <div className="flex justify-between items-center text-sm border-b border-slate-700 pb-2">
                <span className="text-slate-400">{t.driverName}</span>
                <span className="font-medium text-slate-200">{submittedLead.formData.fullName}</span>
              </div>
              <div className="flex justify-between items-center text-sm border-b border-slate-700 pb-2">
                <span className="text-slate-400">{t.licenseClassLabel}</span>
                <Badge variant="outline" className="border-amber-500/40 text-amber-400">
                  {submittedLead.formData.licenseClass} ({submittedLead.formData.provinceOfIssue})
                </Badge>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-400">{t.docsUploaded}</span>
                <span className="font-medium text-emerald-400">
                  {Object.values(submittedLead.documents).flat().length} {t.filesAttached}
                </span>
              </div>
            </div>

            <div className="text-xs text-slate-400 text-center leading-relaxed">
              {t.confirmationNote} <strong className="text-slate-200">dispatch.ironroute@gmail.com</strong> ou 647-745-1234.
            </div>

            <Button
              className="w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-3 rounded-xl"
              onClick={() => {
                setSubmittedLead(null);
                captureGeolocation();
              }}
            >
              {t.submitAnother}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-6 px-3 sm:px-6">
      {/* Toast Notifications */}
      <Toaster position="top-center" richColors />

      <div className="max-w-3xl mx-auto space-y-5">

        {/* Mobile-Optimized Iron Route Header */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-500 via-amber-500 to-red-500"></div>
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-4 mb-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white flex items-center gap-2">
                <span className="text-amber-500">IRON ROUTE</span> LOGISTICS CORP
              </h1>
              <p className="text-[11px] font-bold text-amber-500 tracking-wider uppercase mt-0.5">
                {t.tagline}
              </p>
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-3">
              {/* Bilingual EN / FR Switcher Button */}
              <button
                type="button"
                onClick={() => setLang(prev => prev === 'EN' ? 'FR' : 'EN')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 active:bg-amber-500/30 text-amber-400 font-extrabold text-xs rounded-xl border border-amber-500/30 shadow-sm transition-all cursor-pointer select-none"
              >
                <i className="fas fa-globe text-sm"></i>
                <span>{lang === 'EN' ? '🇨🇦 Français' : '🇨🇦 English'}</span>
              </button>

              <div className="hidden sm:block text-right text-xs text-slate-400 space-y-1">
                <div><i className="fas fa-envelope text-amber-500 mr-1.5"></i>dispatch.ironroute@gmail.com</div>
                <div><i className="fas fa-phone text-amber-500 mr-1.5"></i>647-745-1234</div>
              </div>
            </div>
          </div>

          <div className="bg-slate-950/80 p-3 rounded-xl border border-slate-800 flex items-center justify-between">
            <h2 className="text-xs sm:text-sm font-extrabold text-amber-400 tracking-wide uppercase">
              {t.formTitle}
            </h2>
          </div>
        </div>

        {/* Validation Banner if missing fields */}
        {validationError && (
          <div className="bg-rose-500/15 border border-rose-500/40 text-rose-300 p-4 rounded-xl text-xs font-semibold flex items-center gap-2">
            <i className="fas fa-circle-exclamation text-base text-rose-400"></i>
            <span>{validationError}</span>
          </div>
        )}

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>

          {/* Section 1: Driver Information */}
          <Card className="bg-slate-900/90 border-slate-800 text-slate-100 shadow-xl rounded-2xl">
            <CardHeader className="border-b border-slate-800 p-4 bg-slate-900/50">
              <div className="text-sm font-bold tracking-wider uppercase text-amber-400 flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center text-xs">1</span>
                {t.sec1Title}
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.fullName}</Label>
                  <Input
                    placeholder={t.fullNamePlaceholder}
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.dob}</Label>
                  <Input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleInputChange('dob', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.phone}</Label>
                  <Input
                    type="tel"
                    placeholder={t.phonePlaceholder}
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.email}</Label>
                  <Input
                    type="email"
                    placeholder={t.emailPlaceholder}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-medium text-slate-300">{t.homeAddress}</Label>
                  <Input
                    placeholder={t.homeAddressPlaceholder}
                    value={formData.homeAddress}
                    onChange={(e) => handleInputChange('homeAddress', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.city}</Label>
                  <Input
                    placeholder={t.cityPlaceholder}
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:col-span-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-300">{t.province}</Label>
                    <select
                      value={formData.province}
                      onChange={(e) => handleInputChange('province', e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl px-3 focus:border-amber-500 outline-none"
                    >
                      {CANADIAN_PROVINCES.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-300">{t.country}</Label>
                    <select
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl px-3 focus:border-amber-500 outline-none"
                    >
                      <option value="Canada">Canada</option>
                      <option value="United States">United States</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-slate-300">{t.postalCode}</Label>
                    <Input
                      placeholder={t.postalCodePlaceholder}
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.emergencyName}</Label>
                  <Input
                    placeholder={t.emergencyNamePlaceholder}
                    value={formData.emergencyContactName}
                    onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.emergencyPhone}</Label>
                  <Input
                    type="tel"
                    placeholder={t.emergencyPhonePlaceholder}
                    value={formData.emergencyContactPhone}
                    onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

              </div>
            </CardContent>
          </Card>

          {/* Section 2: Licensing & Credentials */}
          <Card className="bg-slate-900/90 border-slate-800 text-slate-100 shadow-xl rounded-2xl">
            <CardHeader className="border-b border-slate-800 p-4 bg-slate-900/50">
              <div className="text-sm font-bold tracking-wider uppercase text-amber-400 flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center text-xs">2</span>
                {t.sec2Title}
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.licenseNumber}</Label>
                  <Input
                    placeholder={t.licenseNumberPlaceholder}
                    value={formData.licenseNumber}
                    onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.licenseClass}</Label>
                  <Input
                    placeholder={t.licenseClassPlaceholder}
                    value={formData.licenseClass}
                    onChange={(e) => handleInputChange('licenseClass', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.provinceOfIssue}</Label>
                  <Input
                    placeholder={t.provinceOfIssuePlaceholder}
                    value={formData.provinceOfIssue}
                    onChange={(e) => handleInputChange('provinceOfIssue', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.licenseExpiry}</Label>
                  <Input
                    type="date"
                    value={formData.licenseExpiryDate}
                    onChange={(e) => handleInputChange('licenseExpiryDate', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.yearsDriving}</Label>
                  <select
                    value={formData.yearsDriving}
                    onChange={(e) => handleInputChange('yearsDriving', e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl px-3 focus:border-amber-500 outline-none"
                  >
                    <option value="1-2 years">{t.years1_2}</option>
                    <option value="3-5 years">{t.years3_5}</option>
                    <option value="5-10 years">{t.years5_10}</option>
                    <option value="10+ years">{t.years10Plus}</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.endorsements}</Label>
                  <Input
                    placeholder={t.endorsementsPlaceholder}
                    value={formData.endorsements}
                    onChange={(e) => handleInputChange('endorsements', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

              </div>
            </CardContent>
          </Card>

          {/* Section 3: Availability */}
          <Card className="bg-slate-900/90 border-slate-800 text-slate-100 shadow-xl rounded-2xl">
            <CardHeader className="border-b border-slate-800 p-4 bg-slate-900/50">
              <div className="text-sm font-bold tracking-wider uppercase text-amber-400 flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center text-xs">3</span>
                {t.sec3Title}
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.availability}</Label>
                  <select
                    value={formData.availability}
                    onChange={(e) => handleInputChange('availability', e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl px-3 focus:border-amber-500 outline-none"
                  >
                    <option value="Full-time">{t.availFullTime}</option>
                    <option value="Part-time">{t.availPartTime}</option>
                    <option value="Weekend Only">{t.availWeekend}</option>
                    <option value="Casual / On-Call">{t.availCasual}</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.startDate}</Label>
                  <Input
                    type="date"
                    value={formData.earliestStartDate}
                    onChange={(e) => handleInputChange('earliestStartDate', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-medium text-slate-300">{t.experience}</Label>
                  <textarea
                    rows={3}
                    placeholder={t.experiencePlaceholder}
                    value={formData.vanDeliveryExperience}
                    onChange={(e) => handleInputChange('vanDeliveryExperience', e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 text-slate-100 text-base sm:text-sm rounded-xl p-3 focus:border-amber-500 outline-none"
                  />
                </div>

              </div>
            </CardContent>
          </Card>

          {/* Section 4: Required Documents (Camera & Photo Scanner) */}
          <Card className="bg-slate-900/90 border-slate-800 text-slate-100 shadow-xl rounded-2xl">
            <CardHeader className="border-b border-slate-800 p-4 bg-slate-900/50">
              <div className="text-sm font-bold tracking-wider uppercase text-amber-400 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center text-xs">4</span>
                  {t.sec4Title}
                </span>
                <span className="text-[11px] font-normal text-slate-400">{t.scanOrSnap}</span>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              {requiredDocs.map(docType => {
                const attached = documents[docType.id] || [];
                return (
                  <div key={docType.id} className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <i className={`fas ${docType.icon} text-amber-400 text-sm`}></i>
                          <h4 className="text-sm font-semibold text-slate-100">{docType.title}</h4>
                        </div>
                        <p className="text-xs text-slate-400">{docType.description}</p>
                      </div>

                      {/* Mobile Phone Camera Button */}
                      <label className="cursor-pointer inline-flex items-center gap-1.5 px-3.5 py-2 bg-amber-600 hover:bg-amber-500 active:bg-amber-700 text-white rounded-xl text-xs font-semibold shadow-md transition-colors flex-shrink-0">
                        <i className="fas fa-camera text-sm"></i>
                        <span>{t.uploadBtn}</span>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          capture="environment"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileUpload(docType.id, e.target.files)}
                        />
                      </label>
                    </div>

                    {/* File Attachment Previews */}
                    {attached.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-slate-800">
                        {attached.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between bg-slate-900 border border-slate-800 p-2 rounded-lg text-xs">
                            <div className="flex items-center gap-2 truncate">
                              {doc.type === 'image' ? (
                                <img src={doc.dataUrl} alt={doc.name} className="w-8 h-8 object-cover rounded" />
                              ) : (
                                <i className="fas fa-file-pdf text-red-400 text-lg"></i>
                              )}
                              <div className="truncate">
                                <p className="font-medium text-slate-200 truncate">{doc.name}</p>
                                <p className="text-slate-500 text-[10px]">{doc.size}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeDocument(docType.id, doc.id)}
                              className="text-slate-400 hover:text-red-400 p-1.5"
                            >
                              <i className="fas fa-xmark text-sm"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Section 5: Consent & Signature */}
          <Card className="bg-slate-900/90 border-slate-800 text-slate-100 shadow-xl rounded-2xl">
            <CardHeader className="border-b border-slate-800 p-4 bg-slate-900/50">
              <div className="text-sm font-bold tracking-wider uppercase text-amber-400 flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center text-xs">5</span>
                {t.sec5Title}
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-5">

              {/* Consent Checkbox */}
              <div className="flex items-start gap-3 bg-slate-950 border border-slate-800 p-4 rounded-xl">
                <input
                  type="checkbox"
                  id="consentCheckbox"
                  checked={formData.consentGiven}
                  onChange={(e) => handleInputChange('consentGiven', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-slate-700 bg-slate-900 text-amber-500 focus:ring-amber-500 cursor-pointer"
                />
                <label htmlFor="consentCheckbox" className="text-xs text-slate-300 leading-relaxed cursor-pointer select-none">
                  {t.consentLabel}
                </label>
              </div>

              {/* Signature Area */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                
                {/* Touch Signature Pad */}
                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-medium text-slate-300">{t.signature}</Label>
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="text-xs text-amber-400 hover:underline p-1"
                    >
                      {t.clearPad}
                    </button>
                  </div>

                  <div className="space-y-2">
                    <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden relative h-32 touch-none">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={128}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className="w-full h-full cursor-crosshair touch-none"
                      />
                      {!signatureImage && (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs pointer-events-none">
                          {t.drawHere}
                        </div>
                      )}
                    </div>

                    <Input
                      placeholder={t.typeSignature}
                      value={formData.signatureName}
                      onChange={(e) => handleInputChange('signatureName', e.target.value)}
                      className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl font-serif italic"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-slate-300">{t.date}</Label>
                  <Input
                    type="date"
                    value={formData.signatureDate}
                    onChange={(e) => handleInputChange('signatureDate', e.target.value)}
                    className="bg-slate-950 border-slate-800 text-slate-100 text-base sm:text-sm h-11 sm:h-10 rounded-xl"
                  />
                </div>

              </div>

            </CardContent>
          </Card>

          {/* Submit Button - Direct Call */}
          <Button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="w-full bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 hover:from-amber-500 hover:to-red-500 active:from-amber-700 active:to-red-700 text-white font-bold py-4 h-14 text-base rounded-2xl shadow-xl shadow-amber-600/25 transition-all cursor-pointer"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="fas fa-circle-notch fa-spin text-lg"></i> {t.submitting}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="fas fa-paper-plane text-lg"></i> {t.submitBtn}
              </span>
            )}
          </Button>

        </form>

        {/* PDF Footer Reference */}
        <div className="text-center text-[11px] text-slate-500 pt-2 pb-6">
          IRON ROUTE LOGISTICS CORP · dispatch.ironroute@gmail.com · 647-745-1234
        </div>

      </div>
    </div>
  );
}
